/**
 * EnhancedVerificationPolicyCenterSection -- Issue #270.
 *
 * Renders the enhanced verification (SMS + face) policy center inside the
 * `/settings` page:
 *   - High-risk scene matrix backed by GET /api/v1/admin/enhanced-verification/policies
 *   - PATCH editor for SMS policy fields (writable only with
 *     `verification_policy:write` or `*:*`).
 *   - Provider readiness panel for `wechat_face`; `enabled` is rendered as a
 *     read-only "禁用 (第一版)" badge. No enable toggle is exposed.
 *   - Compliance blockers list rendered from the API response -- entries are
 *     displayed as `禁用 (待合规专项 Issue)`; no enable action.
 *   - Recent fallback work orders and audit events.
 *
 * 红线：
 *   - 严禁在前端伪造启用人脸验证或 enabled=true 的提交。
 *   - 严禁渲染任何 SMS template id、AccessKey、provider secret、原始人脸数据。
 *   - 编辑表单的目标值必须经 service 校验；前端不接受 face mode = enabled。
 *   - 回输场景必须保留「姓名 + 身份证」人工核对说明；前端在表单 schema 校验。
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useEnhancedVerificationCenter,
  type EnhancedVerificationPolicyDto,
  type EnhancedVerificationProviderReadinessDto,
} from '@/hooks/useEnhancedVerification';
import {
  canReadEnhancedVerification,
  canWriteVerificationPolicies,
  canWriteVerificationProviderReadiness,
} from '@/lib/enhancedVerificationPermissions';

const FACE_MODES = ['disabled', 'reserved'] as const;
const FACE_COMPLIANCE_STATUSES = [
  'not_started',
  'collecting_materials',
  'blocked',
  'approved_for_future_issue',
] as const;
const RATE_LIMIT_POLICIES = ['default', 'strict', 'critical_lock'] as const;
const FAILURE_LOCK_POLICIES = [
  'escalate_to_work_order',
  'lock_account',
  'require_manual_review',
] as const;
const PROVIDER_HEALTH_STATUSES = [
  'not_evaluated',
  'materials_in_review',
  'ready',
  'blocked',
] as const;

const PROVIDER_KEY_LITERALS = ['wechat_face'] as const;

const policySchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    smsRequired: z.boolean(),
    smsTemplateKey: z
      .string()
      .regex(
        /^[a-z][a-z0-9_]{2,118}$/,
        '短信模板 key 必须为 3..119 位小写字母/数字/下划线，首字母为字母',
      ),
    rateLimitPolicy: z.enum(RATE_LIMIT_POLICIES),
    failureLockPolicy: z.enum(FAILURE_LOCK_POLICIES),
    approvalRequired: z.boolean(),
    fallbackWorkOrderRequired: z.boolean(),
    humanCheckRedline: z.string().min(8, '人工核对红线至少 8 字').max(500),
    faceMode: z.enum(FACE_MODES),
    faceProviderKey: z
      .union([z.enum(PROVIDER_KEY_LITERALS), z.null()])
      .optional(),
    faceComplianceStatus: z.enum(FACE_COMPLIANCE_STATUSES),
    notes: z.string().max(1000).optional().nullable(),
    reason: z.string().min(4, '原因至少 4 字').max(1000),
  })
  .refine(
    (v) =>
      v.faceMode === 'disabled' ? !v.faceProviderKey : Boolean(v.faceProviderKey),
    {
      message: 'reserved 模式必须选择 provider，disabled 模式必须为空',
      path: ['faceProviderKey'],
    },
  );

type PolicyFormValues = z.infer<typeof policySchema>;

const providerSchema = z.object({
  expectedVersion: z.number().int().min(1),
  contractReady: z.boolean(),
  securityAssessmentReady: z.boolean(),
  authorizationTextReady: z.boolean(),
  privacyPolicyReady: z.boolean(),
  deletionMechanismReady: z.boolean(),
  healthStatus: z.enum(PROVIDER_HEALTH_STATUSES),
  complianceBlockerNotes: z.string().max(1000).optional().nullable(),
  reason: z.string().min(4, '原因至少 4 字').max(1000),
});

type ProviderFormValues = z.infer<typeof providerSchema>;

export interface EnhancedVerificationPolicyCenterSectionProps {
  readonly permissions: readonly string[];
}

export default function EnhancedVerificationPolicyCenterSection({
  permissions,
}: EnhancedVerificationPolicyCenterSectionProps): ReactElement | null {
  // Permission gate runs before any hook that would issue backend requests.
  // If the caller has no verification_* permission we return immediately
  // without invoking useEnhancedVerificationCenter, so the hidden section
  // never triggers unauthorized GETs through the typed API client.
  if (!canReadEnhancedVerification(permissions)) return null;
  return <EnhancedVerificationPolicyCenterSectionBody permissions={permissions} />;
}

function EnhancedVerificationPolicyCenterSectionBody({
  permissions,
}: EnhancedVerificationPolicyCenterSectionProps): ReactElement {
  const center = useEnhancedVerificationCenter();
  const canWritePolicies = canWriteVerificationPolicies(permissions);
  const canWriteProvider = canWriteVerificationProviderReadiness(permissions);

  return (
    <section
      className="space-y-3"
      data-testid="settings-enhanced-verification-section"
      aria-label="增强验证策略中心"
    >
      <div>
        <h3 className="text-lg font-medium">增强验证（短信/人脸）设置</h3>
        <p className="text-sm text-muted-foreground">
          第一版以短信二次验证为高风险场景的强制基线，人脸验证仅做禁用/预留契约
          与合规阻塞展示。回输等关键节点必须保留「人工姓名 + 身份证号码」二次核对
          红线；短信/人脸均不替代医疗、财务、权限的人工复核与审计。
        </p>
      </div>

      <PolicyMatrixCard
        center={center}
        canWritePolicies={canWritePolicies}
      />

      <ProviderReadinessCard
        center={center}
        canWriteProvider={canWriteProvider}
      />

      <ComplianceBlockersCard center={center} />

      <FallbackWorkOrdersCard center={center} />

      <AuditEventsCard center={center} />
    </section>
  );
}

interface CenterProps {
  readonly center: ReturnType<typeof useEnhancedVerificationCenter>;
}

function PolicyMatrixCard({
  center,
  canWritePolicies,
}: CenterProps & { readonly canWritePolicies: boolean }): ReactElement {
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">高风险场景矩阵</CardTitle>
        <CardDescription>
          来自 GET <code>/api/v1/admin/enhanced-verification/policies</code>。
          {center.policies.data
            ? <span data-testid="settings-ev-policy-count">{` 共 ${center.policies.data.length} 个场景`}</span>
            : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {center.policies.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-policies-loading">
            正在加载…
          </div>
        )}
        {center.policies.error && (
          <div
            className="text-sm text-destructive"
            role="alert"
            data-testid="settings-ev-policies-error"
          >
            {center.policies.error}
          </div>
        )}
        {center.policies.data && center.policies.data.length === 0 && !center.policies.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-policies-empty">
            后端尚未为当前 tenant/org 初始化增强验证策略。
          </div>
        )}
        {center.policies.data && center.policies.data.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y border text-sm"
              data-testid="settings-ev-policies-table"
            >
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">场景</th>
                  <th className="px-2 py-2 text-left font-medium">风险</th>
                  <th className="px-2 py-2 text-left font-medium">短信</th>
                  <th className="px-2 py-2 text-left font-medium">限流</th>
                  <th className="px-2 py-2 text-left font-medium">失败处理</th>
                  <th className="px-2 py-2 text-left font-medium">人脸 mode</th>
                  <th className="px-2 py-2 text-left font-medium">合规状态</th>
                  <th className="px-2 py-2 text-left font-medium">fallback 工单</th>
                  <th className="px-2 py-2 text-left font-medium">version</th>
                  <th className="px-2 py-2 text-left font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {center.policies.data.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`settings-ev-policy-row-${row.sceneKey}`}
                    className="border-t align-top"
                  >
                    <td className="px-2 py-2">
                      <div className="font-medium">{row.displayName}</div>
                      <div className="text-xs text-muted-foreground">{row.sceneKey}</div>
                      <div className="text-xs">{row.description}</div>
                      <div
                        className="mt-1 rounded bg-muted/40 p-1 text-xs"
                        data-testid={`settings-ev-policy-redline-${row.sceneKey}`}
                      >
                        红线：{row.humanCheckRedline}
                      </div>
                    </td>
                    <td className="px-2 py-2">{row.riskLevel}</td>
                    <td className="px-2 py-2">
                      {row.smsRequired ? '强制' : '可选'}
                      <div className="text-xs text-muted-foreground">{row.smsTemplateKey}</div>
                    </td>
                    <td className="px-2 py-2">{row.rateLimitPolicy}</td>
                    <td className="px-2 py-2">{row.failureLockPolicy}</td>
                    <td className="px-2 py-2">
                      <span
                        className={
                          row.faceMode === 'disabled'
                            ? 'rounded bg-muted px-1 py-0.5 text-xs'
                            : 'rounded bg-amber-100 px-1 py-0.5 text-xs'
                        }
                        data-testid={`settings-ev-policy-face-mode-${row.sceneKey}`}
                      >
                        {row.faceMode === 'disabled'
                          ? '禁用 (第一版)'
                          : `预留 (provider: ${row.faceProviderKey ?? '--'})`}
                      </span>
                    </td>
                    <td className="px-2 py-2">{row.faceComplianceStatus}</td>
                    <td className="px-2 py-2">
                      {row.fallbackWorkOrderRequired ? '必填' : '可选'}
                    </td>
                    <td className="px-2 py-2">{row.version}</td>
                    <td className="px-2 py-2">
                      {canWritePolicies ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setEditing((prev) => (prev === row.sceneKey ? null : row.sceneKey))
                          }
                          data-testid={`settings-ev-policy-edit-${row.sceneKey}`}
                        >
                          {editing === row.sceneKey ? '收起' : '编辑'}
                        </Button>
                      ) : (
                        <span
                          className="text-xs text-muted-foreground"
                          data-testid={`settings-ev-policy-readonly-${row.sceneKey}`}
                        >
                          只读
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canWritePolicies && editing && center.policies.data && (
          <PolicyEditor
            sceneKey={editing}
            policy={center.policies.data.find((r) => r.sceneKey === editing)}
            onSubmit={async (values) => {
              await center.updatePolicy(editing, values);
              setEditing(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface PolicyEditorProps {
  readonly sceneKey: string;
  readonly policy: EnhancedVerificationPolicyDto | undefined;
  readonly onSubmit: (values: PolicyFormValues) => Promise<void>;
}

function PolicyEditor({ sceneKey, policy, onSubmit }: PolicyEditorProps): ReactElement | null {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const defaultValues = useMemo<PolicyFormValues | undefined>(() => {
    if (!policy) return undefined;
    return {
      expectedVersion: policy.version,
      smsRequired: policy.smsRequired,
      smsTemplateKey: policy.smsTemplateKey,
      rateLimitPolicy: policy.rateLimitPolicy as PolicyFormValues['rateLimitPolicy'],
      failureLockPolicy: policy.failureLockPolicy as PolicyFormValues['failureLockPolicy'],
      approvalRequired: policy.approvalRequired,
      fallbackWorkOrderRequired: policy.fallbackWorkOrderRequired,
      humanCheckRedline: policy.humanCheckRedline,
      faceMode: policy.faceMode as PolicyFormValues['faceMode'],
      faceProviderKey: policy.faceProviderKey,
      faceComplianceStatus: policy.faceComplianceStatus as PolicyFormValues['faceComplianceStatus'],
      notes: policy.notes ?? '',
      reason: '',
    };
  }, [policy]);
  const form = useForm<PolicyFormValues>({
    resolver: zodResolver(policySchema),
    defaultValues,
  });
  if (!policy || !defaultValues) return null;

  async function handle(values: PolicyFormValues): Promise<void> {
    setError(null);
    setSuccess(null);
    try {
      await onSubmit(values);
      setSuccess(`已更新 ${sceneKey} 策略。`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(handle)}
      className="space-y-2 rounded border bg-muted/30 p-3 text-xs"
      data-testid={`settings-ev-policy-form-${sceneKey}`}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-policy-sms-required-${sceneKey}`}
            {...form.register('smsRequired')}
          />
          强制短信二次验证（critical 场景不可关闭）
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-policy-approval-required-${sceneKey}`}
            {...form.register('approvalRequired')}
          />
          需要审批配合
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-policy-fallback-required-${sceneKey}`}
            {...form.register('fallbackWorkOrderRequired')}
          />
          失败必须开 fallback 工单
        </label>
        <div>
          <Label htmlFor={`sms-template-${sceneKey}`}>短信模板 key（符号化，禁止真实 template id）</Label>
          <Input
            id={`sms-template-${sceneKey}`}
            autoComplete="off"
            data-testid={`settings-ev-policy-sms-template-${sceneKey}`}
            {...form.register('smsTemplateKey')}
          />
          {form.formState.errors.smsTemplateKey && (
            <p className="text-xs text-destructive">{form.formState.errors.smsTemplateKey.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`rate-limit-${sceneKey}`}>限流策略</Label>
          <select
            id={`rate-limit-${sceneKey}`}
            className="block w-full rounded border bg-background px-2 py-1"
            data-testid={`settings-ev-policy-rate-limit-${sceneKey}`}
            {...form.register('rateLimitPolicy')}
          >
            {RATE_LIMIT_POLICIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={`failure-${sceneKey}`}>失败处理</Label>
          <select
            id={`failure-${sceneKey}`}
            className="block w-full rounded border bg-background px-2 py-1"
            data-testid={`settings-ev-policy-failure-lock-${sceneKey}`}
            {...form.register('failureLockPolicy')}
          >
            {FAILURE_LOCK_POLICIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={`face-mode-${sceneKey}`}>人脸 mode</Label>
          <select
            id={`face-mode-${sceneKey}`}
            className="block w-full rounded border bg-background px-2 py-1"
            data-testid={`settings-ev-policy-face-mode-select-${sceneKey}`}
            {...form.register('faceMode')}
          >
            {FACE_MODES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-muted-foreground">
            第一版仅允许 disabled / reserved；启用须另起合规专项 Issue。
          </p>
        </div>
        <div>
          <Label htmlFor={`face-provider-${sceneKey}`}>人脸 provider key（reserved 模式必填）</Label>
          <Input
            id={`face-provider-${sceneKey}`}
            autoComplete="off"
            data-testid={`settings-ev-policy-face-provider-${sceneKey}`}
            placeholder="例如 wechat_face；disabled 模式留空。"
            {...form.register('faceProviderKey')}
          />
          {form.formState.errors.faceProviderKey && (
            <p className="text-xs text-destructive">{form.formState.errors.faceProviderKey.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor={`face-compliance-${sceneKey}`}>人脸合规状态</Label>
          <select
            id={`face-compliance-${sceneKey}`}
            className="block w-full rounded border bg-background px-2 py-1"
            data-testid={`settings-ev-policy-face-compliance-${sceneKey}`}
            {...form.register('faceComplianceStatus')}
          >
            {FACE_COMPLIANCE_STATUSES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor={`redline-${sceneKey}`}>人工核对红线（回输场景必须出现「姓名」「身份证」）</Label>
        <Input
          id={`redline-${sceneKey}`}
          data-testid={`settings-ev-policy-redline-input-${sceneKey}`}
          {...form.register('humanCheckRedline')}
        />
        {form.formState.errors.humanCheckRedline && (
          <p className="text-xs text-destructive">
            {form.formState.errors.humanCheckRedline.message}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor={`notes-${sceneKey}`}>备注（脱敏，禁止 secret / token / template id 关键字）</Label>
        <Input
          id={`notes-${sceneKey}`}
          data-testid={`settings-ev-policy-notes-${sceneKey}`}
          {...form.register('notes')}
        />
      </div>
      <div>
        <Label htmlFor={`reason-${sceneKey}`}>变更原因</Label>
        <Input
          id={`reason-${sceneKey}`}
          data-testid={`settings-ev-policy-reason-${sceneKey}`}
          {...form.register('reason')}
        />
        {form.formState.errors.reason && (
          <p className="text-xs text-destructive">{form.formState.errors.reason.message}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={form.formState.isSubmitting}
          data-testid={`settings-ev-policy-submit-${sceneKey}`}
        >
          {form.formState.isSubmitting ? '提交中…' : '保存更新'}
        </Button>
        {success && (
          <span className="text-xs text-emerald-600" data-testid={`settings-ev-policy-success-${sceneKey}`}>
            {success}
          </span>
        )}
        {error && (
          <span
            className="text-xs text-destructive"
            role="alert"
            data-testid={`settings-ev-policy-error-${sceneKey}`}
          >
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

function ProviderReadinessCard({
  center,
  canWriteProvider,
}: CenterProps & { readonly canWriteProvider: boolean }): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">人脸 provider 就绪状态</CardTitle>
        <CardDescription>
          第一版仅登记 <code>wechat_face</code> 作为候选；<strong>始终禁用 (enabled=false)</strong>，
          不提供启用开关。启用必须另起合规专项 Issue。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {center.providers.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-providers-loading">
            正在加载…
          </div>
        )}
        {center.providers.error && (
          <div
            className="text-sm text-destructive"
            role="alert"
            data-testid="settings-ev-providers-error"
          >
            {center.providers.error}
          </div>
        )}
        {center.providers.data && center.providers.data.length === 0 && !center.providers.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-providers-empty">
            后端尚未为当前 tenant/org 初始化 provider readiness。
          </div>
        )}
        {center.providers.data?.map((provider) => (
          <ProviderRow
            key={provider.id}
            provider={provider}
            canWriteProvider={canWriteProvider}
            updateProvider={center.updateProvider}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface ProviderRowProps {
  readonly provider: EnhancedVerificationProviderReadinessDto;
  readonly canWriteProvider: boolean;
  readonly updateProvider: ReturnType<typeof useEnhancedVerificationCenter>['updateProvider'];
}

function ProviderRow({ provider, canWriteProvider, updateProvider }: ProviderRowProps): ReactElement {
  const [editing, setEditing] = useState(false);
  return (
    <div
      className="rounded border p-3"
      data-testid={`settings-ev-provider-row-${provider.providerKey}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{provider.providerName}</div>
          <div className="text-xs text-muted-foreground">{provider.providerKey}</div>
        </div>
        <span
          className="rounded bg-muted px-2 py-1 text-xs"
          data-testid={`settings-ev-provider-enabled-${provider.providerKey}`}
        >
          enabled=false (第一版禁用)
        </span>
      </div>
      <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
        <li>合同：{provider.contractReady ? '✓ 已签' : '✗ 未签'}</li>
        <li>安全评估：{provider.securityAssessmentReady ? '✓ 已完成' : '✗ 未完成'}</li>
        <li>授权文本：{provider.authorizationTextReady ? '✓ 已就绪' : '✗ 未就绪'}</li>
        <li>隐私政策：{provider.privacyPolicyReady ? '✓ 已就绪' : '✗ 未就绪'}</li>
        <li>删除机制：{provider.deletionMechanismReady ? '✓ 已就绪' : '✗ 未就绪'}</li>
        <li>健康状态：{provider.healthStatus}</li>
      </ul>
      {provider.complianceBlockerNotes && (
        <p
          className="mt-2 text-xs text-muted-foreground"
          data-testid={`settings-ev-provider-notes-${provider.providerKey}`}
        >
          {provider.complianceBlockerNotes}
        </p>
      )}
      {canWriteProvider && (
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing((v) => !v)}
            data-testid={`settings-ev-provider-edit-${provider.providerKey}`}
          >
            {editing ? '收起' : '编辑就绪状态'}
          </Button>
        </div>
      )}
      {editing && (
        <ProviderEditor provider={provider} updateProvider={updateProvider} />
      )}
    </div>
  );
}

interface ProviderEditorProps {
  readonly provider: EnhancedVerificationProviderReadinessDto;
  readonly updateProvider: ReturnType<typeof useEnhancedVerificationCenter>['updateProvider'];
}

function ProviderEditor({ provider, updateProvider }: ProviderEditorProps): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerSchema),
    defaultValues: {
      expectedVersion: provider.version,
      contractReady: provider.contractReady,
      securityAssessmentReady: provider.securityAssessmentReady,
      authorizationTextReady: provider.authorizationTextReady,
      privacyPolicyReady: provider.privacyPolicyReady,
      deletionMechanismReady: provider.deletionMechanismReady,
      healthStatus: provider.healthStatus as ProviderFormValues['healthStatus'],
      complianceBlockerNotes: provider.complianceBlockerNotes ?? '',
      reason: '',
    },
  });

  async function handle(values: ProviderFormValues): Promise<void> {
    setError(null);
    setSuccess(null);
    try {
      await updateProvider(provider.providerKey, values);
      setSuccess('已更新 provider 就绪状态。');
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(handle)}
      className="mt-2 space-y-2 rounded border bg-muted/30 p-3 text-xs"
      data-testid={`settings-ev-provider-form-${provider.providerKey}`}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-provider-contract-${provider.providerKey}`}
            {...form.register('contractReady')}
          />
          合同已签
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-provider-security-${provider.providerKey}`}
            {...form.register('securityAssessmentReady')}
          />
          安全评估已完成
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-provider-authz-${provider.providerKey}`}
            {...form.register('authorizationTextReady')}
          />
          授权文本已就绪
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-provider-privacy-${provider.providerKey}`}
            {...form.register('privacyPolicyReady')}
          />
          隐私政策已就绪
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`settings-ev-provider-deletion-${provider.providerKey}`}
            {...form.register('deletionMechanismReady')}
          />
          删除机制已就绪
        </label>
        <div>
          <Label htmlFor={`provider-health-${provider.providerKey}`}>健康状态</Label>
          <select
            id={`provider-health-${provider.providerKey}`}
            className="block w-full rounded border bg-background px-2 py-1"
            data-testid={`settings-ev-provider-health-${provider.providerKey}`}
            {...form.register('healthStatus')}
          >
            {PROVIDER_HEALTH_STATUSES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor={`provider-notes-${provider.providerKey}`}>合规阻塞备注（禁止 secret / token 关键字）</Label>
        <Input
          id={`provider-notes-${provider.providerKey}`}
          data-testid={`settings-ev-provider-notes-input-${provider.providerKey}`}
          {...form.register('complianceBlockerNotes')}
        />
      </div>
      <div>
        <Label htmlFor={`provider-reason-${provider.providerKey}`}>变更原因</Label>
        <Input
          id={`provider-reason-${provider.providerKey}`}
          data-testid={`settings-ev-provider-reason-${provider.providerKey}`}
          {...form.register('reason')}
        />
        {form.formState.errors.reason && (
          <p className="text-xs text-destructive">{form.formState.errors.reason.message}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={form.formState.isSubmitting}
          data-testid={`settings-ev-provider-submit-${provider.providerKey}`}
        >
          {form.formState.isSubmitting ? '提交中…' : '保存就绪状态'}
        </Button>
        {success && (
          <span
            className="text-xs text-emerald-600"
            data-testid={`settings-ev-provider-success-${provider.providerKey}`}
          >
            {success}
          </span>
        )}
        {error && (
          <span
            className="text-xs text-destructive"
            role="alert"
            data-testid={`settings-ev-provider-error-${provider.providerKey}`}
          >
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

function ComplianceBlockersCard({ center }: CenterProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">合规阻塞项</CardTitle>
        <CardDescription>
          管理员材料同时包含「保存人脸数据」与「高危强制人脸」诉求，但与 AI GUIDE
          红线冲突。第一版统一显示为合规阻塞，不提供启用开关；启用须另起合规专项
          Issue 完成法务/合规/安全评审。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {center.complianceBlockers.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-blockers-loading">
            正在加载…
          </div>
        )}
        {center.complianceBlockers.error && (
          <div
            className="text-sm text-destructive"
            role="alert"
            data-testid="settings-ev-blockers-error"
          >
            {center.complianceBlockers.error}
          </div>
        )}
        {center.complianceBlockers.data?.map((blocker) => (
          <div
            key={blocker.key}
            className="rounded border border-dashed p-2 text-sm"
            data-testid={`settings-ev-blocker-${blocker.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="font-medium">{blocker.displayName}</div>
              <span
                className="rounded bg-amber-100 px-2 py-0.5 text-xs"
                data-testid={`settings-ev-blocker-status-${blocker.key}`}
              >
                禁用 (待合规专项 Issue)
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{blocker.reason}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FallbackWorkOrdersCard({ center }: CenterProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">最近 fallback 工单</CardTitle>
        <CardDescription>
          验证失败后转人工复核的工单清单（脱敏摘要）。第一版默认仅由后端流程写入，
          管理后台只读展示。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {center.fallbackWorkOrders.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-fallback-loading">
            正在加载…
          </div>
        )}
        {center.fallbackWorkOrders.error && (
          <div
            className="text-sm text-destructive"
            role="alert"
            data-testid="settings-ev-fallback-error"
          >
            {center.fallbackWorkOrders.error}
          </div>
        )}
        {center.fallbackWorkOrders.data && center.fallbackWorkOrders.data.length === 0 &&
          !center.fallbackWorkOrders.loading && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="settings-ev-fallback-empty"
            >
              当前 tenant/org 暂无 fallback 工单。
            </div>
          )}
        {center.fallbackWorkOrders.data && center.fallbackWorkOrders.data.length > 0 && (
          <ul className="space-y-1 text-xs">
            {center.fallbackWorkOrders.data.map((row) => (
              <li
                key={row.id}
                className="rounded border p-2"
                data-testid={`settings-ev-fallback-row-${row.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono">{row.sceneKey}</span>
                    <span className="ml-2 text-muted-foreground">{row.summary}</span>
                  </div>
                  <span className="rounded bg-muted px-2 py-0.5">{row.status}</span>
                </div>
                <div className="mt-1 text-muted-foreground">
                  风险：{row.riskLevel}；操作人哈希：{row.actorUserIdIndex}；创建时间：{row.createdAt}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AuditEventsCard({ center }: CenterProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">最近增强验证审计事件</CardTitle>
        <CardDescription>
          来自 audit_logs (action 前缀 enhanced_verification.*)。操作人 ID 已脱敏。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {center.auditEvents.loading && (
          <div className="text-sm text-muted-foreground" data-testid="settings-ev-audit-loading">
            正在加载…
          </div>
        )}
        {center.auditEvents.error && (
          <div
            className="text-sm text-destructive"
            role="alert"
            data-testid="settings-ev-audit-error"
          >
            {center.auditEvents.error}
          </div>
        )}
        {center.auditEvents.data && center.auditEvents.data.length === 0 &&
          !center.auditEvents.loading && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="settings-ev-audit-empty"
            >
              暂无增强验证审计事件。
            </div>
          )}
        {center.auditEvents.data && center.auditEvents.data.length > 0 && (
          <ul className="space-y-1 text-xs">
            {center.auditEvents.data.map((evt) => (
              <li
                key={evt.id}
                className="rounded border p-2"
                data-testid={`settings-ev-audit-row-${evt.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono">{evt.action}</span>
                  <span className="rounded bg-muted px-2 py-0.5">{evt.riskLevel}</span>
                </div>
                <div className="text-muted-foreground">
                  目标：{evt.targetId ?? '--'}；操作人：{evt.actorUserId ?? '--'}；时间：{evt.createdAt}
                </div>
                {evt.summary && <div className="text-muted-foreground">{evt.summary}</div>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
